import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSyncEvent, getSyncEvents } from '../api';
import { formatTimestamp, formatRelativeTime, getEventTypeLabel, getEventTypeIcon } from '../utils';
import { useFilters, useDebounce, useDataCache } from '../hooks';
import type { SyncEvent, SyncEventFilters, QuarantineReason, FileUploadRequestSummary, SuspiciousFileName } from '../types';
import { EVENT_TYPES, QUARANTINE_REASONS, QUARANTINE_REASON_LABELS } from '../types';
import styles from './Events.module.css';

/**
 * Format raw bytes diagnostic for copy
 */
function formatRawBytesDiagnostic(event: SyncEvent): string {
  const uploadRequest = event.request_summary as FileUploadRequestSummary | undefined;
  const diagnostic = uploadRequest?.upload_validation?.raw_bytes_diagnostic;
  
  if (!diagnostic) return '';
  
  return `
🔬 RAW BYTES DIAGNOSTIC
=======================
Null Byte Count: ${diagnostic.null_byte_count.toLocaleString()}
Null Percentage: ${diagnostic.null_percentage.toFixed(1)}%
${uploadRequest?.upload_validation?.content_length_header !== undefined ? `Content-Length Header: ${uploadRequest.upload_validation.content_length_header} bytes` : ''}
${uploadRequest?.upload_validation?.actual_bytes_received !== undefined ? `Actual Bytes Received: ${uploadRequest.upload_validation.actual_bytes_received} bytes` : ''}

First 200 bytes (readable):
${diagnostic.first_200_readable || '(empty)'}

Last 200 bytes (readable):
${diagnostic.last_200_readable || '(empty)'}

First 200 bytes (hex):
${diagnostic.first_200_hex || '(empty)'}

Last 200 bytes (hex):
${diagnostic.last_200_hex || '(empty)'}
`;
}

/**
 * Format an event for clipboard copy with all details
 */
function formatEventForCopy(event: SyncEvent): string {
  const diagnosticSection = formatRawBytesDiagnostic(event);
  
  return `SYNC EVENT DETAILS
==================
Event ID: ${event.id}
Session ID: ${event.session_id || '-'}
Timestamp: ${formatTimestamp(event.timestamp)}
Event Type: ${event.event_type}
Status: ${event.success ? 'Success' : 'Failed'}${event.was_quarantined ? ' (QUARANTINED)' : ''}
${event.error_message ? `Error: ${event.error_message}` : ''}${event.was_quarantined ? `
Quarantine Reason: ${event.quarantine_reason ? QUARANTINE_REASON_LABELS[event.quarantine_reason] : 'Unknown'}` : ''}

USER & DEVICE
=============
User Email: ${event.user_email || '-'}
Device Code: ${event.device_code || '-'}
Device Name: ${event.device_name || '-'}
Firmware Version: ${event.firmware_version || '-'}
Pico Version: ${event.pico_version || '-'}

FILE INFO
=========
File ID: ${event.file_id || '-'}
File Name: ${event.file_name || '-'}
File Type: ${event.file_type || '-'}
File Size: ${event.file_size_bytes ? `${event.file_size_bytes.toLocaleString()} bytes` : '-'}
Project: ${event.project_name || '-'}
Project ID: ${event.project_id || '-'}

CONTENT INFO
============
Content Size (Raw): ${event.content_size_raw?.toLocaleString() ?? '-'} bytes
Encoding: ${event.content_encoding || '-'}
Hash (Raw): ${event.content_hash_raw || '-'}
Hash (Processed): ${event.content_hash_processed || '-'}
${event.has_suspicious_chars ? `
⚠️ CORRUPTION DETECTED
======================
Suspicious Characters: ${event.suspicious_char_count?.toLocaleString()}
Details: ${event.suspicious_details || '-'}
` : ''}${diagnosticSection}
RAW CONTENT (FROM DEVICE)
=========================
${event.content_raw || '(No raw content)'}

PROCESSED CONTENT (AFTER SERVER PROCESSING)
============================================
${event.content_processed || '(No processed content)'}

REQUEST/RESPONSE
================
Endpoint: ${event.endpoint || '-'}
${event.request_headers && Object.keys(event.request_headers).length > 0 ? `
Request Headers:
${Object.entries(event.request_headers).map(([k, v]) => `  ${k}: ${v}`).join('\n')}
` : ''}
Request Summary:
${event.request_summary ? JSON.stringify(event.request_summary, null, 2) : '(No request summary)'}

Response Summary:
${event.response_summary ? JSON.stringify(event.response_summary, null, 2) : '(No response summary)'}`;
}

/**
 * Format relative time for last updated (e.g., "2 min ago")
 */
function formatLastUpdated(date: Date | null): string {
  if (!date) return 'Never';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  
  const diffHours = Math.floor(diffMin / 60);
  return `${diffHours}h ago`;
}

/**
 * Events Page
 * 
 * List and search all sync events with caching and debounced search.
 */

export default function Events() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setTimeRange: setContextTimeRange } = useFilters();

  // =====================================================
  // Filter state — URL search params are the source of truth
  // so filters, page, and position survive back/forward navigation.
  // =====================================================
  const urlSearch = searchParams.get('search') || searchParams.get('user_email') || searchParams.get('device_code') || '';
  const eventType = searchParams.get('type') || '';
  const timeRange = searchParams.get('time') || '';
  const issueFilter = searchParams.get('issue') || '';
  const deduplicationOnly = searchParams.get('dedup') === '1';
  const suspiciousNamesOnly = searchParams.get('suspicious_names') === '1';
  const nullBytesFilter = (searchParams.get('null_bytes') || 'all') as 'all' | 'any' | 'high' | 'suspicious';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const pageSize = 50;

  // =====================================================
  // Local UI state (non-filter)
  // =====================================================
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [copiedEventId, setCopiedEventId] = useState<string | null>(null);
  const [copyingEventId, setCopyingEventId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearch = useDebounce(searchInput, 300);
  const isInitialMount = useRef(true);
  const loadIdRef = useRef(0); // prevents stale enrichment from overwriting newer page data

  const { fetchPaginatedEvents, getLastFetchTime } = useDataCache();
  const lastFetchTime = getLastFetchTime('paginated');

  // =====================================================
  // URL param helper — uses replace so filter changes
  // don't pollute browser history; the single Events entry
  // always reflects the latest filter state.
  // =====================================================
  const setParam = useCallback((updates: Record<string, string | null>, resetPage = true) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      if (resetPage && !('page' in updates)) {
        next.delete('page');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Sync debounced search → URL (for persistence across navigation)
  const prevDebouncedRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevDebouncedRef.current === debouncedSearch) return;
    prevDebouncedRef.current = debouncedSearch;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      // Normalize user_email / device_code into the generic search param
      next.delete('user_email');
      next.delete('device_code');
      if (debouncedSearch) {
        next.set('search', debouncedSearch);
      } else {
        next.delete('search');
      }
      next.delete('page');
      return next;
    }, { replace: true });
  }, [debouncedSearch, setSearchParams]);

  // =====================================================
  // Client-side filters (applied after API results)
  // =====================================================
  const getNullPercentage = useCallback((event: SyncEvent): number | null => {
    const uploadReq = event.request_summary as FileUploadRequestSummary | undefined;
    return uploadReq?.upload_validation?.raw_bytes_diagnostic?.null_percentage ?? null;
  }, []);

  const getSuspiciousFileName = useCallback((event: SyncEvent): SuspiciousFileName | null => {
    const uploadReq = event.request_summary as FileUploadRequestSummary | undefined;
    return uploadReq?.upload_validation?.suspicious_file_name ?? null;
  }, []);

  // In client-side filter mode, events are already filtered and paginated by
  // applyPoolFilters(). In normal mode, no client-side filtering is needed
  // (all filters are server-side). So filteredEvents is just the events array.
  const filteredEvents = events;

  // =====================================================
  // Build API filters from current state
  // =====================================================

  // Base filters — everything except page/pageSize. Used both for normal
  // server-paginated mode and for the "fetch all" client-side filter mode.
  const buildBaseFilters = useCallback((): SyncEventFilters => {
    const filters: SyncEventFilters = {
      ordering: '-timestamp',
    };

    const effectiveSearch = debouncedSearch.trim();
    if (effectiveSearch) {
      if (effectiveSearch.includes('@')) {
        filters.user_email = effectiveSearch;
      } else {
        filters.device_code = effectiveSearch;
      }
    }

    if (eventType) {
      filters.event_type = eventType as SyncEvent['event_type'];
    }

    if (timeRange) {
      const now = new Date();
      let since: Date;
      switch (timeRange) {
        case '1h': since = new Date(now.getTime() - 60 * 60 * 1000); break;
        case '24h': since = new Date(now.getTime() - 24 * 60 * 60 * 1000); break;
        case '7d': since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
        case '30d': since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); break;
        default: since = new Date(0);
      }
      filters.timestamp_gte = since.toISOString();
    }

    // Unified issue filter → API params
    switch (issueFilter) {
      case 'corrupted':
        filters.has_suspicious_chars = true;
        break;
      case 'corrupted_not_quarantined':
        filters.has_suspicious_chars = true;
        filters.was_quarantined = false;
        break;
      case 'quarantined':
        filters.was_quarantined = true;
        break;
      default:
        // Specific quarantine reason (e.g. SEVERE_TRUNCATION)
        if (issueFilter && (Object.values(QUARANTINE_REASONS) as readonly string[]).includes(issueFilter)) {
          filters.was_quarantined = true;
          filters.quarantine_reason = issueFilter as QuarantineReason;
        }
        break;
    }

    if (deduplicationOnly) {
      filters.has_name_deduplication = true;
    }

    return filters;
  }, [debouncedSearch, eventType, timeRange, issueFilter, deduplicationOnly]);

  // Full filters including page/pageSize — used for normal server-paginated mode
  const buildFilters = useCallback((): SyncEventFilters => ({
    ...buildBaseFilters(),
    page,
    page_size: pageSize,
  }), [buildBaseFilters, page, pageSize]);

  // Whether we need client-side filtering (requires fetching ALL events + enrichment)
  const needsClientSideFiltering = nullBytesFilter !== 'all' || suspiciousNamesOnly;

  // =====================================================
  // Client-side filter pool
  //
  // When client-side filters (null bytes, suspicious names) are active, we
  // fetch ALL matching events from the server, enrich them with full details,
  // apply the client-side filter, and paginate locally. This pool is cached
  // so that page changes or threshold tweaks don't re-fetch everything.
  // =====================================================
  const enrichedPoolRef = useRef<SyncEvent[]>([]);      // all enriched events (pre-filter)
  const enrichedPoolKeyRef = useRef('');                  // server filter key that built this pool
  const [enrichProgress, setEnrichProgress] = useState('');

  // Apply client-side filters to the enriched pool and paginate
  const applyPoolFilters = useCallback(() => {
    let filtered = enrichedPoolRef.current;
    if (suspiciousNamesOnly) {
      filtered = filtered.filter(e => getSuspiciousFileName(e) !== null);
    }
    if (nullBytesFilter !== 'all') {
      filtered = filtered.filter(e => {
        const pct = getNullPercentage(e);
        if (pct === null) return false;
        switch (nullBytesFilter) {
          case 'any': return pct > 0;
          case 'suspicious': return pct > 10;
          case 'high': return pct > 50;
          default: return true;
        }
      });
    }
    setTotalCount(filtered.length);
    const startIdx = (page - 1) * pageSize;
    setEvents(filtered.slice(startIdx, startIdx + pageSize));
  }, [suspiciousNamesOnly, nullBytesFilter, page, pageSize, getNullPercentage, getSuspiciousFileName]);

  // =====================================================
  // Data loading — two modes:
  //   1. Normal: server-paginated, with per-page enrichment
  //   2. Client-side filter: fetch ALL → enrich ALL → filter → paginate locally
  // =====================================================
  const loadEvents = useCallback(async (forceRefresh = false) => {
    const thisLoadId = ++loadIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      if (needsClientSideFiltering) {
        // --- Client-side filter mode ---
        const baseFilters = buildBaseFilters();

        // Add server pre-filter: events with null bytes always have has_suspicious_chars
        if (nullBytesFilter !== 'all' && !baseFilters.has_suspicious_chars) {
          baseFilters.has_suspicious_chars = true;
        }

        const filterKey = JSON.stringify(baseFilters);

        // If the pool is still valid for these server filters, just re-paginate
        if (!forceRefresh && enrichedPoolKeyRef.current === filterKey && enrichedPoolRef.current.length > 0) {
          applyPoolFilters();
          setIsLoading(false);
          return;
        }

        // Need to build a new pool — fetch all pages, then enrich all events
        setIsEnriching(true);
        setEnrichProgress('Fetching events...');

        const allListEvents: SyncEvent[] = [];
        let currentPage = 1;
        let totalListPages = 1;

        do {
          if (thisLoadId !== loadIdRef.current) return;
          setEnrichProgress(`Fetching page ${currentPage}${totalListPages > 1 ? ` of ${totalListPages}` : ''}...`);
          const response = await getSyncEvents({
            ...baseFilters,
            page: currentPage,
            page_size: 200,
          });
          allListEvents.push(...response.results);
          totalListPages = Math.ceil(response.count / 200);
          currentPage++;
        } while (currentPage <= totalListPages);

        if (thisLoadId !== loadIdRef.current) return;

        // Enrich each event with full details
        const concurrency = 10;
        const enriched: SyncEvent[] = [];
        for (let i = 0; i < allListEvents.length; i += concurrency) {
          if (thisLoadId !== loadIdRef.current) return;
          const batch = allListEvents.slice(i, i + concurrency);
          const details = await Promise.all(
            batch.map(e => getSyncEvent(e.id).catch(() => e))
          );
          enriched.push(...details);
          setEnrichProgress(`Loading details: ${enriched.length} / ${allListEvents.length}...`);
        }

        if (thisLoadId !== loadIdRef.current) return;

        // Store in pool and apply filters
        enrichedPoolRef.current = enriched;
        enrichedPoolKeyRef.current = filterKey;
        setIsEnriching(false);
        setEnrichProgress('');
        applyPoolFilters();

      } else {
        // --- Normal server-paginated mode ---
        enrichedPoolRef.current = [];
        enrichedPoolKeyRef.current = '';

        const filters = buildFilters();
        const response = await fetchPaginatedEvents(filters, forceRefresh);
        if (thisLoadId !== loadIdRef.current) return;
        if (response) {
          setEvents(response.results);
          setTotalCount(response.count);

          // Enrich current page with full details (for display richness)
          if (response.results.length > 0) {
            setIsEnriching(true);
            const concurrency = 10;
            const enriched: SyncEvent[] = [];
            for (let i = 0; i < response.results.length; i += concurrency) {
              if (thisLoadId !== loadIdRef.current) return;
              const batch = response.results.slice(i, i + concurrency);
              const details = await Promise.all(
                batch.map(e => getSyncEvent(e.id).catch(() => e))
              );
              enriched.push(...details);
            }
            if (thisLoadId === loadIdRef.current) {
              setEvents(enriched);
              setIsEnriching(false);
            }
          }
        }
      }
    } catch (err) {
      if (thisLoadId !== loadIdRef.current) return;
      console.error('[Events] Error loading:', err);
      setError('Failed to load events');
    } finally {
      if (thisLoadId === loadIdRef.current) {
        setIsLoading(false);
        setIsEnriching(false);
        setEnrichProgress('');
      }
    }
  }, [needsClientSideFiltering, buildBaseFilters, buildFilters, fetchPaginatedEvents, applyPoolFilters, nullBytesFilter]);

  const handleRefresh = useCallback(() => {
    loadEvents(true);
  }, [loadEvents]);

  // Load on mount and when server filters change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
    loadEvents();
  }, [loadEvents]);

  // When page or client-side filter threshold changes while we have a valid pool,
  // just re-slice — no need to re-fetch
  useEffect(() => {
    if (needsClientSideFiltering && enrichedPoolRef.current.length > 0 && !isLoading) {
      applyPoolFilters();
    }
  }, [needsClientSideFiltering, applyPoolFilters, page, nullBytesFilter, suspiciousNamesOnly, isLoading]);

  // =====================================================
  // Scroll position restoration
  // =====================================================
  useEffect(() => {
    if (!isLoading && events.length > 0) {
      const saved = sessionStorage.getItem('events_scroll');
      if (saved) {
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(saved, 10));
        });
        sessionStorage.removeItem('events_scroll');
      }
    }
  }, [isLoading, events.length]);

  // =====================================================
  // Event handlers
  // =====================================================
  const handleEventClick = (eventId: string) => {
    sessionStorage.setItem('events_scroll', String(window.scrollY));
    navigate(`/events/${eventId}`);
  };

  const copyEventToClipboard = async (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCopyingEventId(eventId);
    try {
      const fullEvent = await getSyncEvent(eventId);
      await navigator.clipboard.writeText(formatEventForCopy(fullEvent));
      setCopiedEventId(eventId);
      setTimeout(() => setCopiedEventId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    } finally {
      setCopyingEventId(null);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleClearSearch = () => {
    setSearchInput('');
  };

  // =====================================================
  // CSV Export — fetches ALL pages matching current filters
  // =====================================================
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');

  const handleExportCSV = useCallback(async () => {
    setIsExporting(true);
    setExportProgress('Preparing...');

    try {
      const baseFilters = buildBaseFilters();

      // When client-side filters are active, add the has_suspicious_chars pre-filter
      if (nullBytesFilter !== 'all' && !baseFilters.has_suspicious_chars) {
        baseFilters.has_suspicious_chars = true;
      }

      // Step 1: Fetch all events from the list endpoint
      const listEvents: SyncEvent[] = [];
      let currentPage = 1;
      let totalListPages = 1;
      const batchSize = 200;

      do {
        setExportProgress(`Finding events: page ${currentPage}${totalListPages > 1 ? ` of ${totalListPages}` : ''}...`);
        const response = await getSyncEvents({
          ...baseFilters,
          page: currentPage,
          page_size: batchSize,
        });
        listEvents.push(...response.results);
        totalListPages = Math.ceil(response.count / batchSize);
        currentPage++;
      } while (currentPage <= totalListPages);

      if (listEvents.length === 0) {
        setExportProgress('');
        setIsExporting(false);
        alert('No events to export with the current filters.');
        return;
      }

      // Step 2: Fetch FULL details for each event (the list endpoint omits
      // request_summary, response_summary, content, byte diagnostics, etc.)
      const total = listEvents.length;
      const concurrency = 10;
      const enrichedExport: SyncEvent[] = [];
      let fetched = 0;

      for (let i = 0; i < total; i += concurrency) {
        const batch = listEvents.slice(i, i + concurrency);
        const results = await Promise.all(
          batch.map(e => getSyncEvent(e.id).catch(() => e))
        );
        enrichedExport.push(...results);
        fetched += results.length;
        setExportProgress(`Loading details: ${fetched} / ${total} events...`);
      }

      // Step 3: Apply client-side filters on the ENRICHED data (not list data)
      let exportEvents = enrichedExport;
      if (suspiciousNamesOnly) {
        exportEvents = exportEvents.filter(e => getSuspiciousFileName(e) !== null);
      }
      if (nullBytesFilter !== 'all') {
        exportEvents = exportEvents.filter(e => {
          const pct = getNullPercentage(e);
          if (pct === null) return false;
          switch (nullBytesFilter) {
            case 'any': return pct > 0;
            case 'suspicious': return pct > 10;
            case 'high': return pct > 50;
            default: return true;
          }
        });
      }

      if (exportEvents.length === 0) {
        setExportProgress('');
        setIsExporting(false);
        alert('No events to export with the current filters.');
        return;
      }

      setExportProgress(`Generating CSV (${exportEvents.length.toLocaleString()} events)...`);

      // Build CSV — comprehensive: every field from the SyncEvent model
      const csvHeaders = [
        // Identity & Timing
        'Event ID',
        'Session ID',
        'Timestamp',
        'Duration (ms)',
        'Event Type',
        'Endpoint',

        // User & Device
        'User Email',
        'Device Code',
        'Device Name',
        'Firmware Version',
        'Pico Version',
        'Is First Sync',

        // File Context
        'File Name',
        'File ID',
        'File Type',
        'File Size (bytes)',
        'Project Name',
        'Project ID',

        // Outcome
        'Success',
        'Status Code',
        'Error Message',

        // Quarantine
        'Was Quarantined',
        'Quarantine Reason',
        'Quarantine Reason Label',

        // Corruption Detection
        'Has Corruption',
        'Suspicious Char Count',
        'Suspicious Details',

        // Upload Validation
        'Validation Passed',
        'Size Mismatch',
        'Content-Length Header (bytes)',
        'Actual Bytes Received',
        'Expected File Size (bytes)',
        'Multipart Overhead (bytes)',
        'Conflict Upload',
        'Original File ID',

        // Raw Bytes Diagnostic
        'Null Byte Count',
        'Null Byte %',
        'First 200 Bytes (readable)',
        'Last 200 Bytes (readable)',
        'First 200 Bytes (hex)',
        'Last 200 Bytes (hex)',

        // Suspicious File Name
        'Suspicious File Name',
        'Suspicious Name Issue',
        'Suspicious Name Description',

        // Content Capture
        'Content Size Raw (bytes)',
        'Content Encoding',
        'Content Hash Raw',
        'Content Hash Processed',
        'Content Raw',
        'Content Processed',

        // Name Deduplication
        'Has Deduplication',
        'Dedup Count',
        'Dedup Total Files',
        'Is Mass Dedup',
        'Dedup Details (JSON)',

        // Request / Response (full JSON)
        'Request Summary (JSON)',
        'Response Summary (JSON)',
        'Request Headers (JSON)',
      ];

      const escapeCSV = (val: string | number | boolean | null | undefined): string => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const jsonOrEmpty = (val: unknown): string => {
        if (val === null || val === undefined) return '';
        try { return JSON.stringify(val); } catch { return ''; }
      };

      const csvRows = exportEvents.map(e => {
        const uploadReq = e.request_summary as FileUploadRequestSummary | undefined;
        const validation = uploadReq?.upload_validation;
        const diag = validation?.raw_bytes_diagnostic;
        const suspiciousName = validation?.suspicious_file_name;

        return [
          // Identity & Timing
          e.id,
          e.session_id,
          e.timestamp,
          e.duration_ms,
          e.event_type,
          e.endpoint,

          // User & Device
          e.user_email,
          e.device_code,
          e.device_name,
          e.firmware_version,
          e.pico_version,
          e.is_first_sync,

          // File Context
          e.file_name,
          e.file_id,
          e.file_type,
          e.file_size_bytes,
          e.project_name,
          e.project_id,

          // Outcome
          e.success,
          e.status_code,
          e.error_message,

          // Quarantine
          e.was_quarantined,
          e.quarantine_reason,
          e.quarantine_reason ? QUARANTINE_REASON_LABELS[e.quarantine_reason] : '',

          // Corruption Detection
          e.has_suspicious_chars,
          e.suspicious_char_count,
          e.suspicious_details,

          // Upload Validation
          validation?.validation_passed ?? '',
          validation?.size_mismatch ?? '',
          validation?.content_length_header ?? '',
          validation?.actual_bytes_received ?? '',
          validation?.expected_file_size ?? '',
          validation?.multipart_overhead ?? '',
          uploadReq?.conflict_upload ?? '',
          uploadReq?.original_file_id ?? '',

          // Raw Bytes Diagnostic
          diag?.null_byte_count ?? '',
          diag?.null_percentage != null ? diag.null_percentage.toFixed(1) : '',
          diag?.first_200_readable ?? '',
          diag?.last_200_readable ?? '',
          diag?.first_200_hex ?? '',
          diag?.last_200_hex ?? '',

          // Suspicious File Name
          suspiciousName?.file_name ?? '',
          suspiciousName?.issue ?? '',
          suspiciousName?.description ?? '',

          // Content Capture
          e.content_size_raw,
          e.content_encoding,
          e.content_hash_raw,
          e.content_hash_processed,
          e.content_raw,
          e.content_processed,

          // Name Deduplication
          e.has_name_deduplication,
          e.name_deduplication_count,
          e.name_deduplication_total_files,
          e.is_mass_deduplication,
          jsonOrEmpty(e.name_deduplication_details),

          // Request / Response (full JSON)
          jsonOrEmpty(e.request_summary),
          jsonOrEmpty(e.response_summary),
          jsonOrEmpty(e.request_headers),
        ].map(escapeCSV).join(',');
      });

      const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');

      // Build descriptive filename
      const datePart = new Date().toISOString().slice(0, 10);
      const filterParts: string[] = [];
      if (issueFilter) filterParts.push(issueFilter.toLowerCase().replace(/_/g, '-'));
      if (eventType) filterParts.push(eventType.replace(/_/g, '-'));
      if (timeRange) filterParts.push(`last-${timeRange}`);
      if (debouncedSearch.trim()) filterParts.push('filtered');
      if (deduplicationOnly) filterParts.push('dedup');
      if (suspiciousNamesOnly) filterParts.push('suspicious-names');
      const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
      const filename = `sync-events${filterSuffix}_${datePart}.csv`;

      // Trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportProgress(`Exported ${exportEvents.length.toLocaleString()} events`);
      setTimeout(() => setExportProgress(''), 3000);
    } catch (err) {
      console.error('[Events] Export failed:', err);
      setExportProgress('Export failed');
      setTimeout(() => setExportProgress(''), 3000);
    } finally {
      setIsExporting(false);
    }
  }, [buildBaseFilters, suspiciousNamesOnly, nullBytesFilter, getSuspiciousFileName, getNullPercentage, issueFilter, eventType, timeRange, debouncedSearch, deduplicationOnly]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Sync Events</h1>
          <p className={styles.subtitle}>
            {totalCount.toLocaleString()} events found
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>
            {exportProgress || `Updated ${formatLastUpdated(lastFetchTime)}`}
          </span>
          <button 
            className={styles.exportButton}
            onClick={handleExportCSV}
            disabled={isExporting || isLoading || totalCount === 0}
            title={`Export all ${totalCount.toLocaleString()} matching events as CSV`}
          >
            {isExporting ? (
              <><span className={`${styles.refreshIcon} ${styles.spinning}`}>↻</span> Exporting...</>
            ) : (
              <><span className={styles.exportIcon}>↓</span> Export CSV</>
            )}
          </button>
          <button 
            className={styles.refreshButton} 
            onClick={handleRefresh}
            disabled={isLoading}
            title="Refresh data"
          >
            <span className={`${styles.refreshIcon} ${isLoading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            placeholder="Search by user email or device code..."
            className={styles.searchInput}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button 
              className={styles.clearSearchButton}
              onClick={handleClearSearch}
              title="Clear search"
            >
              ✕
            </button>
          )}
          {isLoading && searchInput !== debouncedSearch && (
            <span className={styles.searchingIndicator}>...</span>
          )}
        </div>
        <div className={styles.filterGroup}>
          <select 
            className={styles.select} 
            value={eventType}
            onChange={(e) => setParam({ type: e.target.value || null })}
          >
            <option value="">All Event Types</option>
            <option value={EVENT_TYPES.DEVICE_LINK_CONFLICT}>⚠️ Device Link Conflict</option>
            <option value={EVENT_TYPES.DEVICE_LINK_CLAIMED}>🔗 Device Link Claimed</option>
            <option value={EVENT_TYPES.DEVICE_AUTHENTICATED}>🔐 Device Authenticated</option>
            <option value={EVENT_TYPES.SYNC_HANDSHAKE}>Sync Handshake</option>
            <option value={EVENT_TYPES.FILE_UPLOAD}>File Upload</option>
            <option value={EVENT_TYPES.FILE_DOWNLOAD}>File Download</option>
            <option value={EVENT_TYPES.CONFLICT_CREATED}>⚡ Conflict Created</option>
            <option value={EVENT_TYPES.CONFLICT_RESOLVED}>✅ Conflict Resolved</option>
            <option value={EVENT_TYPES.ERROR}>Error</option>
          </select>
          <select 
            className={styles.select}
            value={timeRange}
            onChange={(e) => {
              setParam({ time: e.target.value || null });
              setContextTimeRange(e.target.value);
            }}
          >
            <option value="">All Time</option>
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <select 
            className={styles.select}
            value={issueFilter}
            onChange={(e) => setParam({ issue: e.target.value || null })}
          >
            <option value="">All Status</option>
            <optgroup label="Corruption">
              <option value="corrupted">⚠️ Any Corruption</option>
              <option value="corrupted_not_quarantined">⚠️ Corrupted (not quarantined)</option>
            </optgroup>
            <optgroup label="Quarantine Type">
              <option value="quarantined">🔒 All Quarantined</option>
              <option value="SEVERE_TRUNCATION">✂️ Truncated</option>
              <option value="EMPTY_UPLOAD">📭 Empty Upload</option>
              <option value="TLS_BUFFER_LEAK">🔒 TLS Buffer Leak</option>
              <option value="MDNS_CONTAMINATION">🔒 mDNS Contamination</option>
              <option value="FILESYSTEM_METADATA">🔒 File System Metadata</option>
              <option value="SYNC_QUEUE_LEAK">🔒 Sync Queue Leak</option>
              <option value="HIGH_CORRUPTION_RATIO">🔴 High Corruption Ratio</option>
            </optgroup>
          </select>
          <label className={styles.checkbox}>
            <input 
              type="checkbox" 
              checked={deduplicationOnly}
              onChange={(e) => setParam({ dedup: e.target.checked ? '1' : null })}
            />
            <span>🔀 With Deduplication</span>
          </label>
          <label className={styles.checkbox}>
            <input 
              type="checkbox" 
              checked={suspiciousNamesOnly}
              onChange={(e) => setParam({ suspicious_names: e.target.checked ? '1' : null })}
            />
            <span>🏷️ Suspicious File Names</span>
          </label>
          <select
            className={styles.select}
            value={nullBytesFilter}
            onChange={(e) => setParam({ null_bytes: e.target.value === 'all' ? null : e.target.value })}
          >
            <option value="all">All Null Byte Levels</option>
            <option value="any">🔍 Any Null Bytes (&gt;0%)</option>
            <option value="suspicious">⚠️ Suspicious (&gt;10%)</option>
            <option value="high">🔴 High Null Content (&gt;50%)</option>
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button className={styles.retryButton} onClick={handleRefresh}>
            Retry
          </button>
        </div>
      )}

      {isEnriching && needsClientSideFiltering && (
        <div className={styles.enrichingBanner}>
          {enrichProgress || 'Loading detail data for filters…'}
        </div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && events.length === 0 ? (
          <div className={styles.loading}>Loading events...</div>
        ) : events.length === 0 ? (
          <div className={styles.emptyState}>No events found matching your filters.</div>
        ) : (
          <>
            {/* Desktop Table View */}
            <table className={`${styles.table} ${isLoading ? styles.tableLoading : ''}`}>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Type</th>
                  <th>Session ID</th>
                  <th>User</th>
                  <th>Device</th>
                  <th>File</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => (
                  <tr 
                    key={event.id} 
                    className={`${styles.clickableRow} ${event.has_suspicious_chars ? styles.corruptedRow : ''}`}
                    onClick={() => handleEventClick(event.id)}
                  >
                    <td>
                      <div className={styles.timestamp}>
                        <span className={styles.timestampFull}>{formatTimestamp(event.timestamp)}</span>
                        <span className={styles.timestampRelative}>{formatRelativeTime(event.timestamp)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={styles.eventType}>
                        <span className={styles.eventIcon}>{getEventTypeIcon(event.event_type)}</span>
                        {getEventTypeLabel(event.event_type)}
                      </span>
                    </td>
                    <td className={styles.sessionCell}>
                      <span className={styles.sessionId} title={event.session_id || 'No session'}>
                        {event.session_id ? event.session_id.slice(0, 8) + '...' : '-'}
                      </span>
                    </td>
                    <td className={styles.userCell}>
                      {event.user_email || '-'}
                    </td>
                    <td>
                      <span className={styles.deviceCode}>{event.device_code || '-'}</span>
                      {event.device_name && (
                        <span className={styles.deviceName}>{event.device_name}</span>
                      )}
                    </td>
                    <td className={styles.fileCell}>
                      {event.file_name || '-'}
                    </td>
                    <td className={styles.statusCell}>
                      <div className={styles.statusBadges}>
                        {event.was_quarantined ? (
                          <span 
                            className={
                              event.quarantine_reason === 'SEVERE_TRUNCATION' ? styles.truncationBadge : 
                              event.quarantine_reason === 'EMPTY_UPLOAD' ? styles.emptyUploadBadge :
                              styles.quarantinedBadge
                            } 
                            title={event.quarantine_reason ? QUARANTINE_REASON_LABELS[event.quarantine_reason] : 'Quarantined'}
                          >
                            {event.quarantine_reason === 'SEVERE_TRUNCATION' ? '✂️ Truncated' : 
                             event.quarantine_reason === 'EMPTY_UPLOAD' ? '📭 Empty' :
                             '🔒 Quarantined'}
                          </span>
                        ) : event.has_suspicious_chars ? (
                          <span className={styles.corruptedBadge}>⚠️ Corrupted</span>
                        ) : event.success ? (
                          <span className={styles.successBadge}>✓ OK</span>
                        ) : (
                          <span className={styles.errorBadge}>✗ Failed</span>
                        )}
                        {event.is_mass_deduplication && (
                          <span className={styles.massDeduplicationBadge} title="Mass deduplication detected">
                            🚨 Mass Dedup
                          </span>
                        )}
                        {event.has_name_deduplication && !event.is_mass_deduplication && (
                          <span className={styles.deduplicationBadge} title={`${event.name_deduplication_count} files renamed`}>
                            🔀 Dedup
                          </span>
                        )}
                        {/* Suspicious file name badge */}
                        {(() => {
                          const suspicious = getSuspiciousFileName(event);
                          if (!suspicious) return null;
                          return (
                            <span className={styles.suspiciousNameBadge} title={suspicious.description}>
                              🏷️ Suspicious Name
                            </span>
                          );
                        })()}
                        {/* Null byte warning badge for file uploads */}
                        {(() => {
                          const uploadReq = event.request_summary as FileUploadRequestSummary | undefined;
                          const nullPct = uploadReq?.upload_validation?.raw_bytes_diagnostic?.null_percentage;
                          if (nullPct == null) return null;
                          if (nullPct > 50) {
                            return (
                              <span className={styles.highNullBadge} title={`${nullPct.toFixed(1)}% null bytes`}>
                                🔴 {nullPct.toFixed(0)}% Null
                              </span>
                            );
                          }
                          if (nullPct > 10) {
                            return (
                              <span className={styles.suspiciousNullBadge} title={`${nullPct.toFixed(1)}% null bytes`}>
                                ⚠️ {nullPct.toFixed(0)}% Null
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td className={styles.copyCell}>
                      <button
                        className={styles.copyRowButton}
                        onClick={(e) => copyEventToClipboard(event.id, e)}
                        title="Copy all event details"
                        disabled={copyingEventId === event.id}
                      >
                        {copyingEventId === event.id ? '⏳' : copiedEventId === event.id ? '✓' : '📋'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className={`${styles.mobileCards} ${isLoading ? styles.tableLoading : ''}`}>
              {filteredEvents.map((event) => (
                <div 
                  key={event.id} 
                  className={`${styles.mobileCard} ${event.has_suspicious_chars ? styles.mobileCardCorrupted : ''}`}
                  onClick={() => handleEventClick(event.id)}
                >
                  <div className={styles.mobileCardHeader}>
                    <span className={styles.mobileCardType}>
                      <span className={styles.eventIcon}>{getEventTypeIcon(event.event_type)}</span>
                      {getEventTypeLabel(event.event_type)}
                    </span>
                    <span className={styles.mobileCardTime}>{formatRelativeTime(event.timestamp)}</span>
                  </div>
                  <div className={styles.mobileCardBody}>
                    {event.file_name && (
                      <div className={styles.mobileCardFile}>📄 {event.file_name}</div>
                    )}
                    <div className={styles.mobileCardMeta}>
                      <span>{event.user_email || 'Unknown user'}</span>
                      <span className={styles.mobileCardDevice}>{event.device_code || '-'}</span>
                    </div>
                  </div>
                  <div className={styles.mobileCardFooter}>
                    {event.was_quarantined ? (
                      <span className={
                        event.quarantine_reason === 'SEVERE_TRUNCATION' ? styles.truncationBadge : 
                        event.quarantine_reason === 'EMPTY_UPLOAD' ? styles.emptyUploadBadge :
                        styles.quarantinedBadge
                      }>
                        {event.quarantine_reason === 'SEVERE_TRUNCATION' ? '✂️ Truncated' : 
                         event.quarantine_reason === 'EMPTY_UPLOAD' ? '📭 Empty' :
                         '🔒 Quarantined'}
                      </span>
                    ) : event.has_suspicious_chars ? (
                      <span className={styles.corruptedBadge}>⚠️ Corrupted</span>
                    ) : event.success ? (
                      <span className={styles.successBadge}>✓ OK</span>
                    ) : (
                      <span className={styles.errorBadge}>✗ Failed</span>
                    )}
                    {event.is_mass_deduplication && (
                      <span className={styles.massDeduplicationBadge}>🚨</span>
                    )}
                    {/* Suspicious file name badge */}
                    {(() => {
                      const suspicious = getSuspiciousFileName(event);
                      if (!suspicious) return null;
                      return <span className={styles.suspiciousNameBadge} title={suspicious.description}>🏷️</span>;
                    })()}
                    {/* Null byte warning badge for file uploads */}
                    {(() => {
                      const uploadReq = event.request_summary as FileUploadRequestSummary | undefined;
                      const nullPct = uploadReq?.upload_validation?.raw_bytes_diagnostic?.null_percentage;
                      if (nullPct == null) return null;
                      if (nullPct > 50) {
                        return <span className={styles.highNullBadge} title={`${nullPct.toFixed(1)}% null bytes`}>🔴</span>;
                      }
                      if (nullPct > 10) {
                        return <span className={styles.suspiciousNullBadge} title={`${nullPct.toFixed(1)}% null bytes`}>⚠️</span>;
                      }
                      return null;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            onClick={() => setParam({ page: String(Math.max(1, page - 1)) }, false)}
            disabled={page === 1 || isLoading}
          >
            ← Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            className={styles.pageButton}
            onClick={() => setParam({ page: String(Math.min(totalPages, page + 1)) }, false)}
            disabled={page === totalPages || isLoading}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
